/* ============================================================
   Trendout — collection.js

   Rewritten for Shopify. The client's original (kept intact in
   _reference/assets/js/collection.js) held a hardcoded 12-product array and
   rendered the grid with `grid.innerHTML = ...`, which would overwrite the
   real Liquid-rendered products. Filtering, facets, active pills and quick
   view were all faked against that array.

   This batch covers sorting and load-more. Faceted filtering and quick view
   are rebuilt against Shopify's own data in later batches.

   Everything here is progressive: sorting is a real GET form and "Load more"
   is a real link to the next page, so both work with JS disabled. The script
   only upgrades them.
   ============================================================ */
(function () {
  'use strict';

  var T = window.Trendout || {};
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

  var root = $('[data-collection-root]');
  if (!root) return;

  var sectionId = root.getAttribute('data-section-id');

  /* The Section Rendering API returns the section inside its own
     <div id="shopify-section-..."> wrapper. Injecting that verbatim buries the real
     markup one level deeper, which breaks any direct-child CSS — `.recent-rail > a`
     stops matching and the rail's cards blow up to full width; `.quickview` stops seeing
     its two grid children. Always unwrap before inserting. */
  function sectionInner(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var wrap = doc.querySelector('.shopify-section') || doc.body;
    return wrap.innerHTML;
  }

  /* ============================================================
     Sort — submit the form on change
     ============================================================ */
  var sortForm = $('[data-sort-form]');
  var sortSelect = $('[data-sort]');
  if (sortForm && sortSelect) {
    sortSelect.addEventListener('change', function () {
      /* Returning to page 1 on a sort change: staying on page 4 of a different
         ordering shows an arbitrary slice of results. */
      var params = new URLSearchParams(window.location.search);
      params.set('sort_by', sortSelect.value);
      params.delete('page');
      window.location.search = params.toString();
    });
  }

  /* ============================================================
     Load more — append the next page in place
     ============================================================
     Fetches the next page through the Section Rendering API and moves its
     cards into the current grid, so the markup appended is the same markup
     the server would have rendered. The button keeps working as a plain link
     if anything fails.
     ============================================================ */
  function bindLoadMore() {
    var btn = $('[data-load-more]');
    if (!btn) return;

    btn.addEventListener('click', function (e) {
      /* Let modified clicks (new tab, download) behave normally. */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();

      var href = btn.getAttribute('href');
      if (!href || btn.getAttribute('aria-busy') === 'true') return;

      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');

      var url = href + (href.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + sectionId;

      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) {
          if (!r.ok) throw new Error('load-more failed');
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          var grid = $('[data-grid]');
          var newGrid = doc.querySelector('[data-grid]');
          if (grid && newGrid) {
            while (newGrid.firstElementChild) grid.appendChild(newGrid.firstElementChild);
          }

          /* Swap the whole load-more block so the progress bar, the count and the
             next href all come from the server rather than being recalculated. */
          var wrap = $('[data-load-more-wrap]');
          var newWrap = doc.querySelector('[data-load-more-wrap]');
          if (wrap) {
            if (newWrap) {
              wrap.replaceWith(newWrap);
              bindLoadMore();
            } else {
              wrap.remove();
            }
          }

          /* Move focus to the first newly added card so keyboard and screen reader
             users are not dropped back at the top of the list. */
          if (grid && newGrid) {
            var firstNew = grid.querySelector('.product-card:not([data-seen])');
            $$('.product-card', grid).forEach(function (c) { c.setAttribute('data-seen', ''); });
            if (firstNew) {
              var link = firstNew.querySelector('.product-card__title');
              if (link) {
                link.setAttribute('tabindex', '-1');
                link.focus({ preventScroll: true });
              }
            }
          }

          /* The reveal helper is an object, not a function — calling it directly is a
             silent no-op. Sweeping picks up anything newly in view. */
          if (T.reveal && typeof T.reveal.sweep === 'function') T.reveal.sweep();
        })
        .catch(function () {
          /* Fall back to a normal navigation rather than stranding the shopper. */
          window.location.href = href;
        })
        .then(function () {
          btn.removeAttribute('aria-busy');
          btn.classList.remove('is-loading');
        });
    });
  }

  bindLoadMore();

  /* ============================================================
     Faceted filtering
     ============================================================
     The sidebar and the mobile sheet are both real GET forms, so filtering works with
     JS off. Here we intercept, rebuild the query from whichever form changed, fetch the
     section, and swap in the pieces that change — grid, sidebar, pills, counts and the
     load-more block. Everything rendered comes from the server, so the filtered view is
     identical to what a full page load would produce.
     ============================================================ */
  var isUpdating = false;

  function formToUrl(form) {
    var params = new URLSearchParams(new FormData(form));
    /* An untouched price slider sitting at its maximum is not a filter — sending it
       would pin the URL to a range and show a "up to X" pill for nothing. */
    var slider = form.querySelector('[data-price-range]');
    if (slider && slider.value === slider.max) params.delete(slider.name);

    /* Filters always return to the first page: page 3 of a narrower result set is
       usually empty. */
    params.delete('page');

    var qs = params.toString();
    return form.getAttribute('action') + (qs ? '?' + qs : '');
  }

  function render(url) {
    if (isUpdating) return;
    isUpdating = true;
    root.setAttribute('aria-busy', 'true');

    var fetchUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + sectionId;

    return fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('filter failed');
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        /* Swapped wholesale so counts, pills, disabled options and the empty state all
           come from Liquid rather than being recomputed here. */
        [
          '[data-grid]',
          '[data-empty-state]',
          '[data-load-more-wrap]',
          '[data-active-pills]',
          '.results-count',
          '.filters__head',
          '[data-filter-badge]'
        ].forEach(function (sel) {
          var next = doc.querySelector(sel);
          var curr = $(sel);
          if (curr && next) curr.replaceWith(next);
          else if (curr && !next) curr.remove();
        });

        /* The facet forms keep their element (and therefore focus) — only their contents
           are replaced, so the option you just clicked does not vanish under the cursor
           while a sheet is open. */
        ['[data-facet-root]', '[data-facet-root-sheet]'].forEach(function (sel) {
          var curr = $(sel);
          var next = doc.querySelector(sel);
          if (curr && next) curr.innerHTML = next.innerHTML;
        });

        /* A filtered grid that arrived where an empty state used to be, or vice versa,
           needs the missing element inserted rather than just replaced. */
        var results = $('[data-grid]') || $('[data-empty-state]');
        if (!results) {
          var fresh = doc.querySelector('[data-grid]') || doc.querySelector('[data-empty-state]');
          var bar = $('.results-bar');
          if (fresh && bar && bar.parentNode) bar.parentNode.insertBefore(fresh, bar.nextSibling);
        }

        window.history.replaceState({}, '', url);
        bindLoadMore();
        bindPriceLabel();
        if (T.reveal && typeof T.reveal.sweep === 'function') T.reveal.sweep();
      })
      .catch(function () {
        window.location.href = url;
      })
      .then(function () {
        isUpdating = false;
        root.removeAttribute('aria-busy');
      });
  }

  /* Change anywhere inside a facet form filters. Delegated, so replaced markup stays
     wired without rebinding. */
  document.addEventListener('change', function (e) {
    var form = e.target.closest('[data-facet-form]');
    if (!form) return;
    render(formToUrl(form));
  });

  /* The price slider fires continuously while dragging; only filter once released. */
  document.addEventListener('input', function (e) {
    var slider = e.target.closest('[data-price-range]');
    if (!slider) return;
    updatePriceLabel(slider);
  });

  function updatePriceLabel(slider) {
    var form = slider.closest('form');
    var label = form && form.querySelector('[data-price-max]');
    if (!label || !T.formatMoney) return;
    label.textContent = T.formatMoney(slider.value * 100);
  }

  function bindPriceLabel() {
    $$('[data-price-range]').forEach(updatePriceLabel);
  }
  bindPriceLabel();

  /* Active-filter pills and "clear all" are ordinary links; intercept so they update in
     place instead of navigating. */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-pill], [data-clear-all]');
    if (!link || !root.contains(link) && !link.closest('.sheet')) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    render(link.getAttribute('href'));
  });

  /* ============================================================
     Mobile sheets
     ============================================================ */
  /* ============================================================
     Quick view
     ============================================================
     The modal shell is already on the page; its contents are fetched per product from
     the trendout-quick-view section. Opening first and filling second means the dialog
     traps focus and can be closed immediately, even on a slow connection.

     The variant picker below is a compact copy of the product page's. It is duplicated
     rather than shared because trendout-product.js is not loaded on this template and is
     owned by the parallel product-page work; worth extracting into one module once both
     tracks land.
     ============================================================ */
  var qvOverlay = $('#quickview');
  var qvState = { variants: [], pos: { color: null, size: null }, color: null, size: null, current: null };

  function qvMatch(color, size) {
    for (var i = 0; i < qvState.variants.length; i++) {
      var v = qvState.variants[i];
      if (qvState.pos.color !== null && color !== null && v.options[qvState.pos.color] !== color) continue;
      if (qvState.pos.size !== null && size !== null && v.options[qvState.pos.size] !== size) continue;
      return v;
    }
    return null;
  }

  function qvSelect(v) {
    if (!v) return;
    qvState.current = v;
    if (qvState.pos.color !== null) qvState.color = v.options[qvState.pos.color];
    if (qvState.pos.size !== null) qvState.size = v.options[qvState.pos.size];

    $$('[data-qv-swatches] [data-color]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-color') === qvState.color));
    });
    $$('[data-qv-sizes] [data-size]').forEach(function (b) {
      var m = qvMatch(qvState.color, b.getAttribute('data-size'));
      b.disabled = !(m && m.available);
      b.setAttribute('aria-pressed', String(b.getAttribute('data-size') === qvState.size));
    });

    var price = $('[data-qv-price]');
    if (price && T.formatMoney) price.textContent = T.formatMoney(v.price);

    var onSale = v.compareAtPrice > v.price;
    var compare = $('[data-qv-compare]');
    if (compare) {
      compare.hidden = !onSale;
      compare.textContent = onSale && T.formatMoney ? T.formatMoney(v.compareAtPrice) : '';
    }
    var save = $('[data-qv-save]');
    if (save) {
      save.hidden = !onSale;
      if (onSale) save.textContent = 'Save ' + Math.round((v.compareAtPrice - v.price) / v.compareAtPrice * 100) + '%';
    }

    if (v.mediaSrc) {
      var img = $('[data-qv-img]');
      if (img) img.src = v.mediaSrc;
    }

    var addBtn = $('[data-qv-add]');
    if (addBtn) {
      addBtn.disabled = !v.available;
      addBtn.setAttribute('data-variant-id', v.id);
      addBtn.textContent = v.available ? 'Add to Cart' : 'Sold out';
    }
  }

  function qvWire() {
    var dataEl = $('[data-qv-variants]');
    if (!dataEl) return;
    try {
      var data = JSON.parse(dataEl.textContent);
      qvState.variants = data.variants || [];
      qvState.pos = data.optionPositions || { color: null, size: null };
    } catch (e) {
      return;
    }

    var first = qvState.variants.filter(function (v) { return v.available; })[0] || qvState.variants[0];
    qvState.color = null;
    qvState.size = null;
    qvSelect(first);

    $$('[data-qv-swatches] [data-color]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var color = btn.getAttribute('data-color');
        var v = qvMatch(color, qvState.size);
        if (!v || !v.available) {
          v = qvState.variants.filter(function (x) {
            return x.options[qvState.pos.color] === color && x.available;
          })[0] || qvMatch(color, null);
        }
        qvSelect(v);
      });
    });

    $$('[data-qv-sizes] [data-size]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        qvSelect(qvMatch(qvState.color, btn.getAttribute('data-size')));
      });
    });

    $$('[data-qv-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var img = $('[data-qv-img]');
        if (img) img.src = btn.getAttribute('data-qv-src');
        $$('[data-qv-thumb]').forEach(function (b) { b.setAttribute('aria-pressed', String(b === btn)); });
      });
    });

    var addBtn = $('[data-qv-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        if (!qvState.current || !qvState.current.available) return;
        T.addToCart(qvState.current.id, 1, { openDrawer: true });
      });
    }
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quickview]');
    if (!trigger || !qvOverlay) return;
    e.preventDefault();

    var handle = trigger.getAttribute('data-quickview');
    var body = $('[data-qv-body]');
    if (!body) return;

    /* Open immediately so the dialog is focusable and dismissible while loading. */
    if (T.openDialog) T.openDialog(qvOverlay);
    body.setAttribute('aria-busy', 'true');

    fetch('/products/' + handle + '?section_id=trendout-quick-view', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('quick view failed');
        return r.text();
      })
      .then(function (html) {
        body.innerHTML = sectionInner(html);
        qvWire();

        /* Name the dialog after the product now that its title exists, so screen readers
           announce something more useful than "Quick view". */
        var title = $('[data-qv-title]');
        var dialog = $('[data-qv-dialog]');
        if (title && dialog) dialog.setAttribute('aria-label', title.textContent.trim());
      })
      .catch(function () {
        /* Sending them to the real product page beats an empty modal. */
        window.location.href = '/products/' + handle;
      })
      .then(function () {
        body.removeAttribute('aria-busy');
      });
  });

  /* ============================================================
     Recently viewed
     ============================================================
     Ids come from this visitor's own browser; the cards are rendered by Shopify from
     live product data, so nothing shown here can be a stale price.
     ============================================================ */
  (function recentlyViewed() {
    var section = $('[data-recently-viewed]');
    var rail = $('[data-recent-rail]');
    if (!section || !rail || !T.recentlyViewed) return;

    var ids = T.recentlyViewed();
    /* Drop the product currently being viewed, if any, and cap the rail. */
    var currentId = T.currentProductId;
    ids = ids.filter(function (id) { return String(id) !== String(currentId); }).slice(0, 8);
    if (!ids.length) return;

    var query = ids
      .map(function (id) { return 'id:' + encodeURIComponent(id); })
      .join('%20OR%20');

    fetch('/search?type=product&q=' + query + '&section_id=trendout-recently-viewed', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('recently viewed failed');
        return r.text();
      })
      .then(function (html) {
        rail.innerHTML = sectionInner(html);
        /* Only reveal once there is something to show. The rail's cards must be direct
           children for `.recent-rail > a` to size them. */
        if (rail.children.length) section.hidden = false;
      })
      .catch(function () { /* the rail simply stays hidden */ });
  })();

  /* openDialog reads the trigger from document.activeElement, so focus returns to the
     button on close without being passed in. */
  $$('[data-open-filter-sheet]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var sheet = $('#filter-sheet');
      if (sheet && T.openDialog) T.openDialog(sheet);
    });
  });

  $$('[data-open-sort-sheet]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var sheet = $('#sort-sheet');
      if (sheet && T.openDialog) T.openDialog(sheet);
    });
  });
})();
